import React, { useState, useMemo, useContext, useCallback } from 'react';
import { Button, Card, CardContent, CardActions, Stack, TextField, Box, Typography } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce } from 'lodash';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { neutral } from '_lex/_foundations/phxColors';
import MultiSelectPicker from 'components/_pickers/MultiSelectPicker';
import type { CreateEisRuleRequest } from '../_types';
import type { CompactTemplate } from '../../Templates/_shared/_types';
import { useTemplates } from '../../Templates/_shared/_data_sources/useTemplates';

interface RuleFormData {
  name: string;
  templateIds: number[];
  description: string;
}

const createRuleSchema = Yup.object()
  .shape({
    name: Yup.string().required(t('performanceReporting.eis.create.form.ruleName.required')),
    templateIds: Yup.array()
      .of(Yup.number())
      .min(1, t('performanceReporting.eis.create.form.template.required'))
      .required(),
    description: Yup.string().default(''),
  })
  .defined() as Yup.ObjectSchema<RuleFormData>;

interface RuleFormProps {
  onCreate: (data: CreateEisRuleRequest) => Promise<void>;
  onCancel: () => void;
  serverErrors?: ServerErrorResponse;
  setServerErrors: (errors: ServerErrorResponse | undefined) => void;
}

const RuleForm: React.FC<RuleFormProps> = ({ onCreate, onCancel, serverErrors, setServerErrors }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');

  const debouncedSetTemplateSearch = useMemo(() => debounce(setTemplateSearch, 300), []);

  const handleTemplateSearchChange = useCallback(
    (value: string) => {
      if (value) {
        debouncedSetTemplateSearch(value);
      } else {
        debouncedSetTemplateSearch.cancel();
        setTemplateSearch('');
      }
    },
    [debouncedSetTemplateSearch],
  );

  const templateParams = useMemo(
    () => ({
      ...(currentOrganizationId ? { organizationId: currentOrganizationId } : {}),
      state: 'ACTIVE',
      isCompactResponse: true,
      ...(templateSearch ? { search: templateSearch } : {}),
    }),
    [currentOrganizationId, templateSearch],
  );
  const templatesQuery = useTemplates(templateParams);
  const allTemplates = templatesQuery.data?.pages.flatMap((page) => page.content as unknown as CompactTemplate[]) || [];
  const isLoadingTemplates = templatesQuery.isFetching;
  const templateOptions = useMemo(
    () => allTemplates.map((template) => ({ value: template.templateId, label: template.templateName })),
    [allTemplates],
  );

  const handleTemplateScrollToBottom = useCallback(() => {
    if (templatesQuery.hasNextPage && !templatesQuery.isFetchingNextPage) {
      templatesQuery.fetchNextPage();
    }
  }, [templatesQuery.hasNextPage, templatesQuery.isFetchingNextPage, templatesQuery.fetchNextPage]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<RuleFormData>({
    defaultValues: {
      name: '',
      templateIds: [],
      description: '',
    },
    resolver: yupResolver(createRuleSchema),
    mode: 'onSubmit',
  });

  const onSubmit = async (data: RuleFormData) => {
    setServerErrors(undefined);
    const isValid = await trigger();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      const request: CreateEisRuleRequest = {
        name: data.name,
        description: data.description || undefined,
        templateIds: data.templateIds,
        organizationId: currentOrganizationId || '',
      };
      await onCreate(request);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && ('errorDetails' in error || 'errorMessage' in error)) {
        setServerErrors(error as ServerErrorResponse);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card
        sx={{
          border: '1px solid',
          borderColor: neutral[200],
          boxShadow: 'none',
        }}
        className='lex-form'
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          autoComplete='off'
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant='h5'
              sx={{ mb: 3, fontWeight: 600 }}
            >
              {t('performanceReporting.eis.create.pageTitle')}
            </Typography>

            {errorHelper.renderAllErrors(serverErrors)}

            <Stack spacing={3}>
              <Controller
                name='name'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={`${t('performanceReporting.eis.create.form.ruleName.label')} *`}
                    fullWidth
                    error={!!errors.name}
                    helperText={(errors.name?.message as string) || ''}
                    disabled={isSubmitting}
                  />
                )}
              />

              <Controller
                name='templateIds'
                control={control}
                render={({ field }) => (
                  <Box>
                    <MultiSelectPicker
                      id='rule-form-templates'
                      label={`${t('performanceReporting.eis.create.form.template.label')} *`}
                      placeholder={t('performanceReporting.eis.create.form.template.placeholder')}
                      value={field.value}
                      onChange={field.onChange}
                      options={templateOptions}
                      loading={isLoadingTemplates}
                      disabled={isSubmitting}
                      serverSideSearch
                      onSearchChange={handleTemplateSearchChange}
                      onMenuScrollToBottom={handleTemplateScrollToBottom}
                    />
                    {errors.templateIds?.message && (
                      <Typography
                        variant='caption'
                        color='error'
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {errors.templateIds.message as string}
                      </Typography>
                    )}
                  </Box>
                )}
              />

              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('performanceReporting.eis.create.form.description.label')}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={(errors.description?.message as string) || ''}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Stack>
          </CardContent>

          <CardActions sx={{ p: 4, pt: 0 }}>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant='outlined'
                color='neutral'
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {t('performanceReporting.eis.create.buttons.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingPosition='end'
              >
                {t('performanceReporting.eis.create.buttons.create')}
              </Button>
            </Box>
          </CardActions>
        </form>
      </Card>
    </Box>
  );
};

export default RuleForm;
